import React from 'react';
import { type StyleProp, type ViewStyle } from 'react-native';

import {
  FormColumn,
  FormFieldLabelWithInfo,
  FormFieldStack,
  FormInput,
  FormRow,
  SubsectionForm,
  StepForm,
  formFieldInStack,
} from '../../../components/forms';
import { PermissionAwareField } from '../../../components/PermissionAwareField';
import { useFormFieldVisibilityContext } from '../../../hooks/useFormFieldVisibilityContext';
import { subsectionHeaderMetricsProps } from '../../../utils/subsectionHeaderMetrics';

import type { SelectedFile } from '../../../components/forms/FileUpload';
import type { SavedAttachment } from '../../../services/attachmentService';
import type { SubsectionFieldDescriptor } from '../../../utils/subsectionHeaderMetrics';

export type RequestFormAdditionalInformationLabelInfo = {
  legendText: string;
  onInfo: () => void;
  infoA11yHint: string;
  a11yDescription: string;
};

export type RequestFormAdditionalInformationShell = 'formSection' | 'stepForm';

export type RequestFormAdditionalInformationProps = {
  shell: RequestFormAdditionalInformationShell;
  sectionId: string;
  requestId: string | number | undefined;
  /** Used by `StepForm` and forwarded to `SubsectionForm` when provided */
  requestStage?: string;
  titleSubtitle?: string;
  alwaysShow?: boolean;
  allowAttachments?: boolean;
  attachments: SavedAttachment[];
  onAttachFiles?: () => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  isEditing: boolean;
  disabled: boolean;
  selectedFiles: SelectedFile[];
  onFilesSelected?: (files: SelectedFile[]) => void;
  isOffline?: boolean;
  onCaptionChange?: (attachmentId: number, caption: string) => void;
  pendingCaptionEdits?: Record<number, string>;
  containerStyle?: StyleProp<ViewStyle>;
  /** `StepForm` only — default true */
  withFieldStack?: boolean;

  fieldId: string;
  permissionRequestStage: string;
  showFullDetails?: boolean;

  inputLabel: string;
  placeholder: string;
  value: string;
  onChangeText: (value: string) => void;
  multiline?: boolean;
  maxLength?: number;
  numberOfLines?: number;
  webMultilineAutoSize?: boolean;
  inputContainerStyle?: StyleProp<ViewStyle>;
  /** Create-style vs opened-request controls */
  inputUseEditableProp: boolean;
  inputDisabled?: boolean;
  inputEditable?: boolean;

  labelWithInfo?: RequestFormAdditionalInformationLabelInfo;
  /** When true, wrap `PermissionAwareField` in `FormRow` / `FormColumn` */
  wrapFieldInRowColumn?: boolean;
  /** When set with `headerValues`, drives fill count + dynamic subtitle on the card header. */
  subsectionFields?: readonly SubsectionFieldDescriptor[];
  headerValues?: Record<string, unknown>;
  /** OP-UX-02 — expand/collapse survives step navigation when set with `sectionId`. */
  persistSectionExpanded?: boolean;
  defaultSectionExpanded?: boolean;
};

/**
 * Shared “Additional Information” card used at the bottom of most request-form steps.
 * Configure `shell`, copy (`titleSubtitle`, labels, placeholder), and attachment wiring per step.
 */
export const RequestFormAdditionalInformation: React.FC<
  RequestFormAdditionalInformationProps
> = ({
  shell,
  sectionId,
  requestId,
  requestStage,
  titleSubtitle = 'Additional notes and supporting documents for this step.',
  alwaysShow,
  allowAttachments = true,
  attachments,
  onAttachFiles,
  onDeleteAttachment,
  isEditing,
  disabled,
  selectedFiles,
  onFilesSelected,
  isOffline,
  onCaptionChange,
  pendingCaptionEdits,
  containerStyle,
  withFieldStack = true,
  fieldId,
  permissionRequestStage,
  showFullDetails,
  inputLabel,
  placeholder,
  value,
  onChangeText,
  multiline = true,
  maxLength,
  numberOfLines,
  webMultilineAutoSize,
  inputContainerStyle,
  inputUseEditableProp,
  inputDisabled,
  inputEditable,
  labelWithInfo,
  wrapFieldInRowColumn = true,
  subsectionFields,
  headerValues,
  persistSectionExpanded,
  defaultSectionExpanded,
}) => {
  const fieldVisibility = useFormFieldVisibilityContext({
    showFullDetails,
    requestStage: permissionRequestStage,
    requestId,
  });

  const headerMetrics =
    subsectionFields != null && headerValues != null
      ? subsectionHeaderMetricsProps(
          subsectionFields,
          headerValues,
          fieldVisibility,
        )
      : undefined;

  const isInputViewOnly = inputUseEditableProp
    ? !(inputEditable ?? false)
    : Boolean(inputDisabled);

  const inputNode = (
    <>
      {labelWithInfo ? (
        <FormFieldLabelWithInfo
          onInfo={labelWithInfo.onInfo}
          infoA11yHint={labelWithInfo.infoA11yHint}
          a11yDescription={labelWithInfo.a11yDescription}
        >
          {labelWithInfo.legendText}
        </FormFieldLabelWithInfo>
      ) : null}
      <FormInput
        testID={`form-input-${fieldId}`}
        label={inputLabel}
        hideLabel={Boolean(labelWithInfo)}
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        multiline={multiline}
        maxLength={maxLength}
        numberOfLines={numberOfLines}
        webMultilineAutoSize={webMultilineAutoSize}
        containerStyle={[formFieldInStack, inputContainerStyle]}
        disabled={isInputViewOnly}
        editable={!isInputViewOnly}
        readOnlyMode={isInputViewOnly}
        ignorePermissions={isInputViewOnly}
        richText={multiline && Boolean(maxLength)}
        requestId={requestId == null ? undefined : String(requestId)}
        requestSection={sectionId}
        isOnline={!isOffline}
      />
    </>
  );

  const permissionField = (
    <PermissionAwareField
      fieldId={fieldId}
      requestStage={permissionRequestStage}
      requestId={requestId}
      showFullDetails={showFullDetails}
    >
      {inputNode}
    </PermissionAwareField>
  );

  const body = wrapFieldInRowColumn ? (
    <FormRow>
      <FormColumn>{permissionField}</FormColumn>
    </FormRow>
  ) : (
    permissionField
  );

  const common = {
    title: 'Additional Information' as const,
    titleSubtitle,
    ...(headerMetrics ?? {}),
    sectionId,
    requestId,
    requestStage,
    allowAttachments,
    attachments,
    inlineFieldHtml: value,
    onAttachFiles,
    onDeleteAttachment,
    isEditing,
    disabled,
    selectedFiles,
    onFilesSelected,
    isOffline,
    onCaptionChange,
    pendingCaptionEdits,
    alwaysShow,
    containerStyle,
    ...(persistSectionExpanded != null ? { persistSectionExpanded } : {}),
    ...(defaultSectionExpanded != null ? { defaultSectionExpanded } : {}),
  };

  if (shell === 'stepForm') {
    return (
      <StepForm {...common} withFieldStack={withFieldStack}>
        {body}
      </StepForm>
    );
  }

  return (
    <SubsectionForm {...common}>
      <FormFieldStack>{body}</FormFieldStack>
    </SubsectionForm>
  );
};

RequestFormAdditionalInformation.displayName =
  'RequestFormAdditionalInformation';
